import React, { type LegacyRef } from 'react'
import EmojiPicker from 'emoji-picker-react'
import useTemplatePreview from './useTemplatePreview'
import './TemplatePreviewStyle.css'
import '../MarketingEmailTemplate.css'
import './TemplatePreviewCanvasPageBg.css'
import { InputField, TextEditor } from '@components/common'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import EmailTemplateFormatPanel from './EmailTemplateFormatPanel.component'
import PageColorSettingField from './PageColorSettingField.component'
import {
  TEMPLATE_EMAIL_STARTER_LAYOUTS,
  VARIABLE_LIST,
} from '@constants/Marketing'
import {
  FEATURE_ROW_ICON_COLOR_SWATCHES,
  normalizeFeatureRowIconHex,
} from '@constants/Marketing/featureRowIcons'
import { sanitizeHtmlContent } from '@utils/sanitizeHtml.util'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'

const TemplatePreview: React.FC = () => {
  const hooks = useTemplatePreview()
  const layoutPanelVisible =
    hooks.emailLayoutPickerOpen && hooks.isEmailTemplate && hooks.isEditing
  const showEmailEmptyOnboarding =
    hooks.isEmailTemplate && hooks.isEmailCanvasEmpty

  const editorOrPreview = showEmailEmptyOnboarding ? (
    <div className="mt-tp-empty-email-onboarding">
      <i className="mgc_compass_3_line" />
      <p className="mt-tp-empty-email-onboarding-title">
        Start your email by selecting an element
      </p>
      <p className="mt-tp-empty-email-onboarding-subtitle">
        Add Text, Button, Image, Layout, or other blocks from the Elements
        panel.
      </p>
    </div>
  ) : hooks.isEditing ? (
    <>
      <TextEditor
        editorInput={hooks.content}
        enableEnterNextLine={true}
        enableMention={false}
        hideToolbar={true}
        emailFormattingToolbar={hooks.isEmailTemplate}
        isAiEnabled={false}
        setEditorInput={hooks.setContent}
        setIsAiEnabled={() => {}}
        toolbarRef={hooks.toolbarRef}
        handleSend={() => {}}
        selectionRef={hooks.selectionRef}
        _variableList={VARIABLE_LIST}
        _variableRef={hooks.variableRef}
      />
    </>
  ) : (
    <div
      className="mt-tp-content-canvas-inner"
      dangerouslySetInnerHTML={{
        __html: sanitizeEmailHtml(hooks.content.htmlContent),
      }}
    />
  )

  return (
    <div className="mt-template-preview-component">
      {hooks.isEmailTemplate && (
        <input
          ref={hooks.emailImageInputRef as LegacyRef<HTMLInputElement>}
          type="file"
          accept="image/*"
          className="mt-tp-email-image-file-input"
          tabIndex={-1}
          onChange={hooks.handleEmailImageFileChange}
          aria-hidden
        />
      )}
      <div
        className={`mt-tp-elements${layoutPanelVisible ? ' mt-tp-elements--layout-active' : ''}${!hooks.isEmailTemplate ? ' mt-tp-elements--sms' : ''}`}
      >
        {!layoutPanelVisible && <span className="mt-tp-header">Elements</span>}
        {layoutPanelVisible && (
          <div
            className="mt-tp-email-starters"
            onMouseDownCapture={() => hooks.snapshotCaretBeforeSidebarClick()}
          >
            <button
              type="button"
              className="mt-tp-starters-back"
              onClick={hooks.closeEmailLayoutPicker}
              aria-label="Back to Elements"
            >
              <span className="ti ti-chevron-left" aria-hidden />
              Elements
            </button>
            <div className="mt-tp-starters-header">
              <span className="mt-tp-starters-label">Layouts</span>
            </div>
            <p className="mt-tp-starters-hint">
              Full templates replace the entire email (Undo if needed). A
              content card inserts at the cursor without replacing everything.
            </p>
            <span className="mt-tp-starters-group-label">Full templates</span>
            <div className="mt-tp-email-starter-cards">
              {TEMPLATE_EMAIL_STARTER_LAYOUTS.map((layout) => (
                <button
                  key={layout.id}
                  type="button"
                  className="mt-tp-email-starter-card mt-tp-email-starter-card--full"
                  onClick={() => hooks.applyEmailStarterLayout(layout.html)}
                >
                  <span className="mt-tp-email-starter-title">
                    {layout.title}
                  </span>
                  <span className="mt-tp-email-starter-desc">
                    {layout.subtitle}
                  </span>
                  <span className="mt-tp-email-starter-badge">
                    Replaces entire email
                  </span>
                </button>
              ))}
            </div>
            <span className="mt-tp-starters-group-label">Add to email</span>
            <div className="mt-tp-email-starter-cards">
              <button
                type="button"
                className="mt-tp-email-starter-card mt-tp-email-starter-card--block"
                onClick={() => hooks.applyEmailSectionBlock()}
              >
                <span className="mt-tp-email-starter-title">Content card</span>
                <span className="mt-tp-email-starter-desc">
                  Title row + body area with light frame (inserts at cursor)
                </span>
              </button>
            </div>
          </div>
        )}
        {!layoutPanelVisible && (
          <div
            className="mt-tp-element-buttons"
            onMouseDownCapture={() => hooks.snapshotCaretBeforeSidebarClick()}
          >
            {hooks.elementButtons.map((button) => (
              <button
                type="button"
                key={button.id}
                className={`unstyled-button mt-tp-element-button${button.active ? ' active' : ''}${button.disabled ? ' disabled' : ''}`}
                title={button.hint}
                disabled={button.disabled}
                onClick={() => hooks.handleElementButtonClick(button)}
              >
                <i className={button.icon} />
                <span>{button.text}</span>
              </button>
            ))}
          </div>
        )}
        {hooks.smsEmojiPickerOpen && !hooks.isEmailTemplate && (
          <div className="mt-tp-sms-emoji-picker">
            <EmojiPicker
              onEmojiClick={(data) => hooks.handleSmsEmojiSelect(data.emoji)}
            />
          </div>
        )}
      </div>
      <div className="mt-tp-content">
        <div className="mt-tp-content-nav-btn">
          <div className="mt-tp-content-nav-left">
            <div className="zoom-dropdown-wrapper">
              <span className="mgc_search_2_line" />
              <DropDownListComponent
                value={hooks.zoom}
                dataSource={[
                  { id: '100', text: '100%' },
                  { id: '150', text: '150%' },
                  { id: '200', text: '200%' },
                ]}
                select={hooks.handleChangeZoom}
                className="e-caret-hide"
              />
              <span className="ti ti-caret-down-filled" />
            </div>
            <ButtonGroup
              className="mt-tp-content-arrow-buttons"
              buttons={[
                {
                  label: '',
                  value: 'undo',
                  disabled: !hooks.canUndo,
                  icon: 'ti ti-arrow-back-up',
                },
                {
                  label: '',
                  value: 'redo',
                  disabled: !hooks.canRedo,
                  icon: 'ti ti-arrow-forward-up',
                },
              ]}
              onButtonClick={hooks.handleUndoRedo}
            />
          </div>
        </div>
        <div className="mt-tp-content-body">
          {hooks.isEmailTemplate &&
            hooks.isEditing &&
            hooks.rowHoverToolbar.visible && (
              <div
                className="mt-tp-row-hover-toolbar"
                style={{
                  top: hooks.rowHoverToolbar.top,
                  left: hooks.rowHoverToolbar.left,
                }}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={hooks.lockRowHoverToolbar}
                onMouseLeave={hooks.unlockRowHoverToolbar}
              >
                <span className="ti ti-grip-vertical mt-tp-row-hover-toolbar-drag" />
                <button
                  type="button"
                  className="mt-tp-row-hover-toolbar-btn"
                  disabled={!hooks.rowHoverToolbar.canMoveUp}
                  onClick={() => hooks.moveHoveredRow('up')}
                  aria-label="Move row up"
                >
                  <span className="ti ti-chevron-up" />
                </button>
                <button
                  type="button"
                  className="mt-tp-row-hover-toolbar-btn"
                  disabled={!hooks.rowHoverToolbar.canMoveDown}
                  onClick={() => hooks.moveHoveredRow('down')}
                  aria-label="Move row down"
                >
                  <span className="ti ti-chevron-down" />
                </button>
                <button
                  type="button"
                  className="mt-tp-row-hover-toolbar-btn danger"
                  onClick={hooks.deleteHoveredRow}
                  aria-label="Delete row"
                >
                  <span className="ti ti-trash" />
                </button>
              </div>
            )}
          {hooks.isEmailTemplate &&
            hooks.isEditing &&
            hooks.featureIconPickerPosition && (
              <div
                className="mt-tp-feature-icon-picker"
                style={{
                  position: 'fixed',
                  top: hooks.featureIconPickerPosition.top,
                  left: hooks.featureIconPickerPosition.left,
                  zIndex: 1210,
                }}
                role="dialog"
                aria-label="Choose feature icon"
              >
                <div className="mt-tp-feature-icon-picker-label">Icon</div>
                <div className="mt-tp-feature-icon-picker-grid">
                  {hooks.featureRowIconChoices.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="mt-tp-feature-icon-picker-option"
                      title={c.label}
                      aria-label={c.label}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => hooks.applyFeatureRowIcon(c.svg)}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtmlContent(c.svg),
                      }}
                    />
                  ))}
                </div>
                <div className="mt-tp-feature-icon-picker-label mt-tp-feature-icon-picker-label--spaced">
                  Color
                </div>
                <p className="mt-tp-feature-icon-picker-hint">
                  Stroke and border use the same color.
                </p>
                <div className="mt-tp-feature-icon-picker-colors">
                  {FEATURE_ROW_ICON_COLOR_SWATCHES.map((hex) => {
                    const active =
                      normalizeFeatureRowIconHex(
                        hooks.featureIconPickerColor,
                      ) === normalizeFeatureRowIconHex(hex)
                    return (
                      <button
                        key={hex}
                        type="button"
                        className={`mt-tp-feature-icon-picker-swatch${active ? ' mt-tp-feature-icon-picker-swatch--active' : ''}`}
                        style={{ backgroundColor: hex }}
                        title={hex}
                        aria-label={`Icon color ${hex}`}
                        aria-pressed={active}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => hooks.applyFeatureRowIconColor(hex)}
                      />
                    )
                  })}
                  <label className="mt-tp-feature-icon-picker-color-native">
                    <span className="visually-hidden">Custom color</span>
                    <input
                      type="color"
                      value={normalizeFeatureRowIconHex(
                        hooks.featureIconPickerColor,
                      )}
                      onChange={(e) =>
                        hooks.applyFeatureRowIconColor(e.target.value)
                      }
                      aria-label="Custom icon color"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="mt-tp-feature-icon-picker-reset-color"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => hooks.resetFeatureRowIconColor()}
                >
                  Reset to default color
                </button>
              </div>
            )}
          <div className="mt-tp-content-canvas-container">
            <div
              className={`mt-tp-content-canvas zoom-${hooks.zoom.replace('%', '')}${hooks.isEmailTemplate ? ' mt-tp-content-canvas--email' : ''}`}
              ref={hooks.previewContainerRef}
            >
              {hooks.isEmailTemplate ? (
                <div
                  className="mt-tp-page-surface"
                  style={hooks.pageSurfaceStyle}
                >
                  <div
                    className={`mt-marketing-email-tpl-root mt-marketing-email-tpl-root--canvas${hooks.isEditing && !showEmailEmptyOnboarding ? ' mt-marketing-email-tpl-root--editor' : ''}`}
                  >
                    {editorOrPreview}
                  </div>
                </div>
              ) : (
                editorOrPreview
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-tp-settings">
        {hooks.showHyperlinkSettings ? (
          <>
            <span className="mt-tp-header">Hyperlink</span>
            <div className="position-relative">
              {hooks.linkStatus === 'Confirm' && (
                <span className="mgc_check_circle_fill" />
              )}
              <InputField
                label="Link Address"
                placeholder="https://www.website.com"
                value={hooks.linkAddress}
                onChange={hooks.handleChangeLinkAddress}
                onBlur={hooks.toggleLinkStatus}
                destructive={!!hooks.hyperlinkError}
                hint={hooks.hyperlinkError ?? undefined}
              />
            </div>
          </>
        ) : (
          <>
            <span className="mt-tp-header">Page settings</span>
            <InputField
              label="Template name"
              placeholder={hooks.selectedTemplate?.name}
              value={hooks.templateName}
              onChange={hooks.handleChangeTemplateName}
              destructive={!!hooks.templateError}
              hint={hooks.templateError ?? undefined}
            />
            {hooks.isEmailTemplate && (
              <>
                <div className="mt-tp-settings-size-block">
                  <span className="mt-tp-settings-subheader">Size</span>
                  <div className="mt-tp-settings-size-row">
                    <InputField
                      label="Width"
                      placeholder="600"
                      value={hooks.pageWidth}
                      onChange={hooks.handleChangePageWidth}
                    />
                    <InputField
                      label="Height"
                      placeholder="Auto"
                      value={hooks.pageHeight}
                      onChange={hooks.handleChangePageHeight}
                    />
                  </div>
                </div>
                <PageColorSettingField
                  value={hooks.pageColor}
                  onChange={hooks.handleChangePageColor}
                />
                {hooks.headerBandApplicable && (
                  <div className="mt-tp-header-band-settings">
                    <PageColorSettingField
                      label="Header band color"
                      value={hooks.headerBandSwatchValue}
                      onChange={hooks.handleChangeHeaderBandColor}
                    />
                    {hooks.headerBandColor.trim() ? (
                      <button
                        type="button"
                        className="mt-tp-header-band-reset"
                        onClick={hooks.resetHeaderBandColorToDefault}
                      >
                        Use layout default
                      </button>
                    ) : null}
                  </div>
                )}
                {hooks.heroCopyBandApplicable && (
                  <div className="mt-tp-header-band-settings">
                    <PageColorSettingField
                      label="Hero text band color"
                      value={hooks.heroCopyBandSwatchValue}
                      onChange={hooks.handleChangeHeroCopyBandColor}
                    />
                    {hooks.heroCopyBandColor.trim() ? (
                      <button
                        type="button"
                        className="mt-tp-header-band-reset"
                        onClick={hooks.resetHeroCopyBandColorToDefault}
                      >
                        Use layout default
                      </button>
                    ) : null}
                  </div>
                )}
                {hooks.ctaButtonApplicable && (
                  <div className="mt-tp-header-band-settings">
                    <PageColorSettingField
                      label="Button 1 fill color"
                      value={hooks.ctaButtonSwatchValue}
                      onChange={hooks.handleChangeCtaButtonBgColor}
                    />
                    {hooks.ctaButtonBgColor.trim() ? (
                      <button
                        type="button"
                        className="mt-tp-header-band-reset"
                        onClick={hooks.resetCtaButtonBgColorToDefault}
                      >
                        Use layout default
                      </button>
                    ) : null}
                    <PageColorSettingField
                      label="Button 1 text color"
                      value={hooks.ctaButtonTextSwatchValue}
                      onChange={hooks.handleChangeCtaButtonTextColor}
                    />
                    {hooks.ctaButtonTextColor.trim() ? (
                      <button
                        type="button"
                        className="mt-tp-header-band-reset"
                        onClick={hooks.resetCtaButtonTextColorToDefault}
                      >
                        Use layout default
                      </button>
                    ) : null}
                    <InputField
                      label="Button 1 label"
                      placeholder="Call to action"
                      value={hooks.ctaButtonLabelSlot1}
                      onChange={hooks.handleChangeCtaButtonLabelSlot1}
                    />
                    {hooks.ctaPageSettingsShowButton2 ? (
                      <>
                        <PageColorSettingField
                          label="Button 2 fill color"
                          value={hooks.ctaButtonAltSwatchValue}
                          onChange={hooks.handleChangeCtaButtonAltBgColor}
                        />
                        {hooks.ctaButtonAltBgColor.trim() ? (
                          <button
                            type="button"
                            className="mt-tp-header-band-reset"
                            onClick={hooks.resetCtaButtonAltBgColorToDefault}
                          >
                            Use layout default
                          </button>
                        ) : null}
                        <PageColorSettingField
                          label="Button 2 text color"
                          value={hooks.ctaButtonAltTextSwatchValue}
                          onChange={hooks.handleChangeCtaButtonAltTextColor}
                        />
                        {hooks.ctaButtonAltTextColor.trim() ? (
                          <button
                            type="button"
                            className="mt-tp-header-band-reset"
                            onClick={hooks.resetCtaButtonAltTextColorToDefault}
                          >
                            Use layout default
                          </button>
                        ) : null}
                        <InputField
                          label="Button 2 label"
                          placeholder="Secondary action"
                          value={hooks.ctaButtonLabelSlot2}
                          onChange={hooks.handleChangeCtaButtonLabelSlot2}
                        />
                      </>
                    ) : null}
                  </div>
                )}
                {hooks.textLinkApplicable && (
                  <div className="mt-tp-header-band-settings">
                    <InputField
                      label="Text link label"
                      placeholder="Text link call to action"
                      value={hooks.textLinkLabel}
                      onChange={hooks.handleChangeTextLinkLabel}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
        {hooks.isEmailTemplate &&
          hooks.isEditing &&
          !showEmailEmptyOnboarding && (
            <EmailTemplateFormatPanel
              rteRef={hooks.toolbarRef}
              defaultFontSize={hooks.pageFontSize}
              onDefaultFontSizeChange={hooks.handleChangePageFontSize}
            />
          )}
      </div>
    </div>
  )
}

export default TemplatePreview
